import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Button,
  InputSearch,
  TableData,
  Status,
  Pagination,
  ActionDropdown,
  ActionDropdownAction,
  ColumnSettings,
} from '@/components';
import { useTableColumns } from '@/hooks/useTableColumns';
import { Column, Model } from '@/types/common';
import {
  getListModel,
  createModel,
  updateModel,
  CreateModelRequest,
  UpdateModelRequest,
  getAreas,
  downloadModelsCsv,
} from '@/services/api/models';
import { getListOem } from '@/services/api/oems';
import { UniversalFilter } from '@/components';
import AddModelModal from './components/AddModelModal';
import {
  showSuccessToast,
  showErrorToast,
} from '@/components/toast/globalToast';
import { extractApiErrorMessage, getUserEmail } from '@/utils';

const ModelsPage: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [modelData, setModelData] = useState<Model[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [total, setTotal] = useState(0);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingModel, setEditingModel] = useState<Model | null>(null);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null
  );
  const [oems, setOems] = useState<any[]>([]);
  const [areas, setAreas] = useState<string[]>([]);
  const [pageSize, setPageSize] = useState(10);
  const [isDownloading, setIsDownloading] = useState(false);
  const [actionDropdownOpen, setActionDropdownOpen] = useState<
    string | number | null
  >(null);

  const handleToggleDropdown = useCallback(
    (id: string | number) => {
      setActionDropdownOpen((prev) => (prev === id ? null : id));
    },
    []
  );

  useEffect(() => {
    const handleOutsideClick = () => setActionDropdownOpen(null);
    if (actionDropdownOpen !== null) {
      document.addEventListener('click', handleOutsideClick);
    }
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [actionDropdownOpen]);

  useEffect(() => {
    const loadOems = async () => {
      try {
        const response = await getListOem({ page: '1', size: '1000' });
        setOems(response.data || []);
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to load OEMs'
        );
        showErrorToast('Error', errorMessage);
      }
    };
    loadOems();
  }, []);

  const loadAreas = useCallback(async () => {
    try {
      const response = await getAreas({ page: '1', size: '1000' });
      setAreas(response?.areas || []);
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to load areas'
      );
      showErrorToast('Error', errorMessage);
    }
  }, []);

  useEffect(() => {
    loadAreas();
  }, [loadAreas]);

  // Filter configuration for UniversalFilter
  const filterConfigs = useMemo(
    () => [
      {
        key: 'oemName',
        title: 'OEM Name',
        icon: 'ri-building-2-line',
        multiple: true,
        dataSource: oems,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'unitType',
        title: 'Unit Type',
        icon: 'ri-car-line',
        multiple: true,
        staticOptions: [
          { key: 'new', label: 'New', value: 'NEW' },
          { key: 'second', label: 'Second', value: 'SECOND' },
        ],
      },
      {
        key: 'contractType',
        title: 'Contract Type',
        icon: 'ri-file-text-line',
        multiple: true,
        staticOptions: [
          { key: 'rto', label: 'Rent To Own', value: 'RTO' },
          { key: 'rent', label: 'Rent', value: 'RENT' },
        ],
      },
      {
        key: 'batteryType',
        title: 'Battery Type',
        icon: 'ri-battery-2-line',
        multiple: true,
        staticOptions: [
          { key: 'charging', label: 'Charging', value: 'CHARGING' },
          { key: 'swap', label: 'Swap', value: 'SWAP' },
          { key: 'both', label: 'Swap & Charging', value: 'BOTH' },
        ],
      },
      {
        key: 'area',
        title: 'Area',
        icon: 'ri-map-pin-line',
        multiple: true,
        staticOptions: areas.map((area, index) => ({
          key: `area_${index}_${area.toLowerCase().replace(/\s+/g, '_')}`,
          label: area,
          value: area,
        })),
      },
    ],
    [oems, areas]
  );

  const fetchModels = useCallback(
    async (
      page: number = 1,
      search?: string,
      filters?: Record<string, any>,
      size?: number
    ) => {
      setIsLoading(true);
      try {
        const params: any = {
          page: page.toString(),
          size: (size || pageSize).toString(),
        };

        if (search?.trim()) {
          params.search = search.trim();
        }

        if (filters?.unitType && filters.unitType.length > 0) {
          params.unitTypes = filters.unitType.join(',');
        }

        if (filters?.contractType && filters.contractType.length > 0) {
          params.contractTypes = filters.contractType.join(',');
        }

        if (filters?.batteryType && filters.batteryType.length > 0) {
          params.batteryTypes = filters.batteryType.join(',');
        }

        if (filters?.oemName && filters.oemName.length > 0) {
          params.oemIDs = filters.oemName.join(',');
        }

        if (filters?.area && filters.area.length > 0) {
          params.areas = filters.area.join(',');
        }

        const response = await getListModel(params);

        setModelData(response.data || []);
        setTotal(response.pagination?.total || 0);

        if (response.pagination?.page) {
          setCurrentPage(response.pagination.page);
        }
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to load Models'
        );
        showErrorToast('Error', errorMessage);
        setModelData([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    [pageSize]
  );

  useEffect(() => {
    fetchModels(currentPage, searchTerm, activeFilters, pageSize);
  }, [fetchModels, currentPage, searchTerm, activeFilters, pageSize]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleSort = (field: string, direction: 'asc' | 'desc' | null) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Client-side sorting
  const sortedModelData = useMemo(() => {
    if (!sortField || !sortDirection) {
      return modelData;
    }

    return [...modelData].sort((a: any, b: any) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue === bValue) return 0;

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
      } else {
        comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [modelData, sortField, sortDirection]);

  const handleEditModel = (model: Model) => {
    setEditingModel(model);
    setIsAddModalOpen(true);
  };

  const handleDownloadCsv = async () => {
    setIsDownloading(true);
    try {
      const params: Record<string, any> = {};

      if (activeFilters?.unitType && activeFilters.unitType.length > 0) {
        params.unitTypes = activeFilters.unitType.join(',');
      }

      if (
        activeFilters?.contractType &&
        activeFilters.contractType.length > 0
      ) {
        params.contractTypes = activeFilters.contractType.join(',');
      }

      if (activeFilters?.batteryType && activeFilters.batteryType.length > 0) {
        params.batteryTypes = activeFilters.batteryType.join(',');
      }

      if (activeFilters?.oemName && activeFilters.oemName.length > 0) {
        params.oemIDs = activeFilters.oemName.join(',');
      }

      if (activeFilters?.area && activeFilters.area.length > 0) {
        params.areas = activeFilters.area.join(',');
      }

      const blob = await downloadModelsCsv(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dateStamp = new Date().toISOString().slice(0, 10);

      link.href = url;
      link.download = `models_${dateStamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccessToast('Success', 'Download started');
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to download models'
      );
      showErrorToast('Error', errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  const columns: Column[] = [
    {
      key: 'oemName',
      title: 'OEM Name',

      width: 'w-32',
      render: (value: string) => (
        <span className="text-[14px] font-medium">{value}</span>
      ),
    },
    {
      key: 'name',
      title: 'Model Name',

      width: 'w-32',
      render: (value: string) => (
        <span className="text-[14px] font-medium">{value}</span>
      ),
    },
    {
      key: 'unitType',
      title: 'Unit Type',
      align: 'center',

      width: 'w-24',
      render: (value: string) => {
        const formattedValue =
          value === 'SECOND' ? 'Second' : value === 'NEW' ? 'New' : value;
        return <span className="text-[14px]">{formattedValue}</span>;
      },
    },
    {
      key: 'contractType',
      title: 'Contract Type',
      align: 'center',

      width: 'w-32',
      render: (value: string) => (
        <Status value={value} type="contract" size="small" />
      ),
    },
    {
      key: 'purchasePrice',
      title: 'Harga Beli',

      align: 'right',
      width: 'w-32',
      render: (value: number) => (
        <span className="text-[14px]">
          Rp. {value ? value.toLocaleString() : '0'}
        </span>
      ),
    },
    {
      key: 'sellingPrice',
      title: 'Harga Jual',

      align: 'right',
      width: 'w-32',
      render: (value: number) => (
        <span className="text-[14px]">
          Rp. {value ? value.toLocaleString() : '0'}
        </span>
      ),
    },
    {
      key: 'deposit',
      title: 'Deposit to OEM',

      align: 'right',
      width: 'w-32',
      render: (value: number) => (
        <span className="text-[14px]">
          Rp. {value ? value.toLocaleString() : '0'}
        </span>
      ),
    },
    {
      key: 'area',
      title: 'Area',

      width: 'w-32',
      render: (value: string) => <span className="text-[14px]">{value}</span>,
    },
    {
      key: 'batteryType',
      title: 'Battery Type',

      align: 'center',
      width: 'w-32',
      render: (value: string) => {
        let displayValue = value;
        if (value === 'BOTH') displayValue = 'Swap & Charging';
        else if (value === 'SWAP') displayValue = 'Swap';
        else if (value === 'CHARGING') displayValue = 'Charging';

        return <span className="text-[14px]">{displayValue}</span>;
      },
    },
    {
      key: 'maxDistancePerBattery',
      title: 'Distance Max per Battery',
      align: 'center',
      width: 'w-40',
      render: (value: number) => (
        <span className="text-[14px]">{value} km</span>
      ),
    },
    {
      key: 'maxWeight',
      title: 'Weight Max',
      align: 'center',
      width: 'w-28',
      render: (value: number) => (
        <span className="text-[14px]">{value} kg</span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 'w-16',
      align: 'center',
      sticky: 'right',
      render: (_value: any, record: Model, index: number) => {
        const uniqueId = record.ID || `temp-${index}`;
        const actions: ActionDropdownAction[] = [
          {
            key: 'edit',
            label: 'Edit',
            icon: 'ri-pencil-line',
            onClick: () => {
              handleEditModel(record);
              setActionDropdownOpen(null);
            },
          },
        ];
        return (
          <ActionDropdown
            record={record}
            isOpen={actionDropdownOpen === uniqueId}
            onToggle={() => handleToggleDropdown(uniqueId)}
            actions={actions}
            ariaLabel={`Actions for model ${(record as any).name}`}
          />
        );
      },
    },
  ];

  // Column customization (show/hide + reorder), persisted per browser.
  const { visibleColumns, columnSettingsProps } = useTableColumns<Model>({
    storageKey: 'models',
    columns,
  });

  const handleSaveModel = async (data: CreateModelRequest) => {
    setIsCreating(true);
    try {
      const userEmail = getUserEmail();

      if (editingModel) {
        const modelId = editingModel?.ID;

        if (!modelId) {
          throw new Error('Model ID is missing - cannot update');
        }

        const updatePayload: UpdateModelRequest = {
          ...data,
          updatedBy: userEmail,
        };

        await updateModel(modelId, updatePayload);
        showSuccessToast('Success', 'Model updated successfully');
      } else {
        // Create mode - create new Model
        const createPayload: CreateModelRequest = {
          ...data,
          createdBy: userEmail,
        };

        await createModel(createPayload);
        showSuccessToast('Success', 'Model created successfully');
      }

      setIsAddModalOpen(false);
      setEditingModel(null);

      // Refresh the Model list and areas (in case user input new area)
      fetchModels(currentPage, searchTerm, activeFilters, pageSize);
      loadAreas();
    } catch (error) {
      const fallbackMessage = `Failed to ${editingModel ? 'update' : 'create'
        } Model`;
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      showErrorToast('Error', errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
              Models
            </h1>
            <p className="text-[14px] text-gray-600">
              Manage vehicle models and specifications.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="white"
              size="small"
              onClick={handleDownloadCsv}
              disabled={isDownloading}
            >
              <i className="ri-download-line text-[12px] mr-1"></i>
              {isDownloading ? 'Downloading...' : 'Download'}
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={() => setIsAddModalOpen(true)}
            >
              <i className="ri-add-line text-[12px] mr-1"></i>
              Create Model
            </Button>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-end gap-2">
          <div className="flex items-center gap-2">
            <InputSearch
              placeholder="Search models..."
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              debounceMs={300}
              size="small"
            />
          </div>

          <UniversalFilter
            filters={filterConfigs}
            currentFilters={activeFilters}
            onApply={(filters: Record<string, any>) => {
              setActiveFilters(filters);
              setCurrentPage(1);
            }}
            onClear={() => {
              setActiveFilters({});
              setCurrentPage(1);
            }}
          />

          <ColumnSettings {...columnSettingsProps} />
        </div>
      </div>

      {/* Data Table with Sticky Actions */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="relative">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2 text-gray-600">Loading Models...</span>
            </div>
          ) : (
            <TableData
              data={sortedModelData}
              columns={visibleColumns}
              sorting={{
                field: sortField,
                direction: sortDirection,
                onSort: handleSort,
              }}
              size="small"
              className="min-w-full"
            />
          )}
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setCurrentPage(1); // Reset to first page when changing page size
          fetchModels(1, searchTerm, activeFilters, newPageSize);
        }}
      />

      {/* Add Model Modal */}
      <AddModelModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingModel(null);
        }}
        onSubmit={handleSaveModel}
        isLoading={isCreating}
        initialData={editingModel}
        isEditMode={!!editingModel}
      />
    </div>
  );
};

export default ModelsPage;
